<template>
  <div class="image-upload-field">
    <!-- Alternar entre Upload e YouTube -->
    <div
      class="upload-type-toggle mb-2"
      v-if="!previewUrl && !value && !hideYoutube"
    >
      <button
        type="button"
        class="btn-toggle"
        :class="{ active: uploadType === 'file' }"
        @click="uploadType = 'file'"
      >
        <i class="ri-upload-2-line"></i> Carregar Ficheiro
      </button>
      <button
        type="button"
        class="btn-toggle"
        :class="{ active: uploadType === 'youtube' }"
        @click="uploadType = 'youtube'"
      >
        <i class="ri-youtube-line"></i> Link do YouTube
      </button>
    </div>

    <!-- Preview da imagem actual/carregada -->
    <div v-if="previewUrl || value" class="image-preview">
      <div v-if="isYoutube(previewUrl || value)" class="youtube-preview">
        <img
          :src="`https://img.youtube.com/vi/${getYoutubeId(
            previewUrl || value,
          )}/0.jpg`"
          alt="YouTube Preview"
          class="preview-media"
        />
        <div class="video-overlay"><i class="ri-play-circle-fill"></i></div>
      </div>
      <video
        v-else-if="isVideo(previewUrl || value)"
        :src="previewUrl || value"
        controls
        class="preview-media"
      ></video>
      <img
        v-else
        :src="previewUrl || value"
        alt="Preview"
        class="preview-media"
      />

      <button
        type="button"
        class="btn-remove-image"
        @click="clear"
        title="Remover"
      >
        <i class="ri-close-circle-fill"></i>
      </button>
    </div>

    <!-- Drop zone (File) -->
    <label
      v-else-if="uploadType === 'file'"
      class="image-dropzone"
      :class="{ 'is-dragging': isDragging, 'is-uploading': uploading }"
      @dragover.prevent="isDragging = true"
      @dragleave.prevent="isDragging = false"
      @drop.prevent="onDrop"
    >
      <input
        type="file"
        :accept="accept"
        class="image-input-hidden"
        ref="fileInput"
        @change="onFileChange"
      />
      <div v-if="uploading" class="upload-state">
        <i class="ri-loader-4-line spin"></i>
        <span>A carregar...</span>
      </div>
      <div v-else class="upload-state">
        <i v-if="accept.includes('video')" class="ri-video-add-line"></i>
        <i v-else class="ri-image-add-line"></i>
        <span>{{ label }}</span>
        <small>Clica ou arrasta aqui · {{ acceptLabel }}</small>
      </div>
    </label>

    <!-- Input link (YouTube) -->
    <div v-else class="youtube-input-zone">
      <div class="input-group">
        <span class="input-group-text"
          ><i class="ri-youtube-fill text-danger"></i
        ></span>
        <input
          type="url"
          v-model="youtubeUrl"
          class="form-control"
          placeholder="https://www.youtube.com/watch?v=..."
          @input="onYoutubeInput"
        />
      </div>
      <small class="text-muted mt-1 d-block"
        >Cole o link do vídeo para associar à capa</small
      >
    </div>

    <!-- Erro de upload -->
    <p v-if="uploadError" class="upload-error">
      <i class="ri-error-warning-line"></i> {{ uploadError }}
    </p>
  </div>
</template>

<script>
import uploadService from "../../services/uploadService";

export default {
  name: "ImageUpload",
  props: {
    /** URL actual (do modelo guardado) */
    value: { type: String, default: "" },
    label: { type: String, default: "Adicionar ficheiro de capa" },
    accept: { type: String, default: "image/*,video/*" },
    acceptLabel: { type: String, default: "Imagens ou Vídeos · max 50MB" },
    hideYoutube: { type: Boolean, default: false },
  },
  emits: ["update:value", "uploaded"],
  data() {
    return {
      previewUrl: "",
      uploading: false,
      isDragging: false,
      uploadError: "",
      uploadType: "file", // 'file' ou 'youtube'
      youtubeUrl: "",
    };
  },
  methods: {
    isVideo(url) {
      if (!url) return false;
      if (this.isYoutube(url)) return true;
      const videoExtensions = [".mp4", ".webm", ".ogg", ".mov"];
      return videoExtensions.some((ext) => url.toLowerCase().includes(ext));
    },
    isYoutube(url) {
      if (!url) return false;
      return url.includes("youtube.com") || url.includes("youtu.be");
    },
    getYoutubeId(url) {
      if (!url) return null;
      const regExp =
        /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
      const match = url.match(regExp);
      return match && match[2].length === 11 ? match[2] : null;
    },
    onYoutubeInput() {
      if (this.isYoutube(this.youtubeUrl)) {
        this.$emit("update:value", this.youtubeUrl);
        this.$emit("uploaded", this.youtubeUrl);
      }
    },
    onFileChange(e) {
      const file = e.target.files[0];
      if (file) this.upload(file);
    },
    onDrop(e) {
      this.isDragging = false;
      const file = e.dataTransfer.files[0];
      if (file) this.upload(file);
    },
    async upload(file) {
      this.uploading = true;
      this.$emit("uploading", true);
      this.uploadError = "";
      this.previewUrl = URL.createObjectURL(file);
      try {
        const url = await uploadService.uploadImage(file);
        this.$emit("update:value", url);
        this.$emit("uploaded", url);
        this.previewUrl = url;
      } catch {
        this.uploadError = "Erro ao carregar a imagem. Tenta novamente.";
        this.previewUrl = "";
      } finally {
        this.uploading = false;
        this.$emit("uploading", false);
      }
    },
    clear() {
      this.previewUrl = "";
      this.youtubeUrl = "";
      this.$emit("update:value", "");
      if (this.$refs.fileInput) this.$refs.fileInput.value = "";
    },
  },
  watch: {
    value(v) {
      if (!v) {
        this.previewUrl = "";
        this.youtubeUrl = "";
      } else if (this.isYoutube(v)) {
        this.uploadType = "youtube";
        this.youtubeUrl = v;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.image-upload-field {
  width: 100%;
}

.image-dropzone {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 140px;
  border: 2px dashed #dfe9ff;
  border-radius: 14px;
  background: #f8faff;
  cursor: pointer;
  transition: all 0.2s;
  user-select: none;

  &:hover,
  &.is-dragging {
    border-color: #1765fd;
    background: #ebf1ff;
  }

  &.is-uploading {
    opacity: 0.7;
    pointer-events: none;
  }
}

.image-input-hidden {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}

.upload-type-toggle {
  display: flex;
  gap: 8px;

  .btn-toggle {
    flex: 1;
    padding: 8px;
    border: 1px solid #dfe9ff;
    background: #fff;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 500;
    color: #4b5563;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    transition: all 0.2s;

    i {
      font-size: 16px;
    }

    &:hover {
      background: #f3f4f6;
    }

    &.active {
      background: #ebf1ff;
      border-color: #1765fd;
      color: #1765fd;
      font-weight: 600;
    }
  }
}

.youtube-input-zone {
  padding: 15px;
  background: #f8faff;
  border: 1px solid #dfe9ff;
  border-radius: 12px;

  .input-group-text {
    background: #fff;
    border-color: #dfe9ff;
  }

  input {
    border-color: #dfe9ff;
    &:focus {
      border-color: #1765fd;
      box-shadow: 0 0 0 3px rgba(23, 101, 253, 0.1);
    }
  }
}

.youtube-preview {
  position: relative;
  width: 100%;

  .video-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.3);
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-size: 48px;
    pointer-events: none;
    opacity: 0.8;
    transition: opacity 0.2s;
  }

  &:hover .video-overlay {
    opacity: 1;
  }
}

.upload-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  color: #6b7280;

  i {
    font-size: 32px;
    color: #1765fd;

    &.spin {
      animation: rotate360 0.8s linear infinite;
    }
  }

  span {
    font-size: 14px;
    font-weight: 600;
    color: #374151;
  }

  small {
    font-size: 11px;
    color: #9ca3af;
  }
}

.image-preview {
  position: relative;
  border-radius: 14px;
  overflow: hidden;
  border: 1px solid #dfe9ff;
  background: #f0f0f0;

  .preview-media {
    width: 100%;
    max-height: 250px;
    object-fit: contain;
    background: #000;
    display: block;
  }

  .btn-remove-image {
    position: absolute;
    top: 8px;
    right: 8px;
    background: rgba(0, 0, 0, 0.55);
    border: none;
    border-radius: 50%;
    width: 30px;
    height: 30px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-size: 20px;
    cursor: pointer;
    transition: background 0.2s;

    &:hover {
      background: #ef4444;
    }
  }
}

.upload-error {
  margin-top: 6px;
  font-size: 12px;
  color: #ef4444;
  display: flex;
  align-items: center;
  gap: 4px;
}

@keyframes rotate360 {
  to {
    transform: rotate(360deg);
  }
}
</style>
